import { Component } from 'react';
import { Button, Card, CardContent } from '@crestrewards/ui';
import { RotateCcw, TriangleAlert } from 'lucide-react';

/**
 * Catches a render crash and shows something recoverable instead of a blank
 * page.
 *
 * React's default is brutal: if any component throws while rendering, React
 * unmounts the **whole tree**. No message, no sidebar, no way back except a
 * browser refresh — and to the person using it, one broken screen looks like
 * the entire console is down.
 *
 * `AppLayout` puts one of these around the routed content, so a crash on one
 * page leaves the sidebar and topbar working and the operator can simply click
 * somewhere else. `AppRoutes` puts a second one around everything, for the
 * rarer case where the shell itself is what broke.
 *
 * **It resets by itself on navigation.** `AppLayout` keys its `<main>` on the
 * pathname, so moving to another page remounts this boundary and clears the
 * error. Nothing has to remember to reset it.
 *
 * **What it cannot catch**, because React does not report them here: errors
 * thrown in event handlers (a click, a submit) and errors inside promises. Those
 * are already handled where they happen — every API call in this console is
 * wrapped in try/catch and renders the normalised message. This is the net for
 * the ones nobody predicted.
 *
 * A class component because that is the only thing React supports for this;
 * there is no hook equivalent.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
    this.handleRetry = this.handleRetry.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // The browser console is the only place a developer can see the stack, and
    // React swallows it once the boundary takes over. There is no logging
    // service in this repo yet — Phase 7's logging review is where that lands.
    console.error('Unhandled render error:', error, info?.componentStack);
  }

  handleRetry() {
    this.setState({ error: null });
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto w-full max-w-[1400px] px-4 py-6 sm:px-6 sm:py-8">
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <TriangleAlert className="h-8 w-8 text-destructive" aria-hidden="true" />

            <p className="text-title font-medium text-foreground">
              This screen ran into a problem
            </p>
            <p className="max-w-md text-meta text-muted-foreground">
              Nothing you did caused it and nothing was saved incorrectly — the page
              itself failed to draw. The rest of the console still works.
            </p>

            <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
              <Button type="button" onClick={this.handleRetry}>
                <RotateCcw aria-hidden="true" />
                Try this screen again
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.reload()}
              >
                Reload the page
              </Button>
            </div>

            {/* Collapsed, because an operator does not need it — but it is the
                first thing whoever fixes this will ask for. */}
            <details className="mt-3 w-full max-w-xl text-left">
              <summary className="cursor-pointer text-label text-muted-foreground">
                Technical details (useful when reporting this)
              </summary>
              <pre className="mt-2 overflow-x-auto rounded-md bg-surface-tint p-3 text-label text-muted-foreground">
                {String(error?.stack || error?.message || error)}
              </pre>
            </details>
          </CardContent>
        </Card>
      </div>
    );
  }
}
